import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import organisationService, {
  fileToDataUrl,
  uploadOrganisationAsset,
} from "../features/organisation/organisationService";
import { getLookup } from "../features/setup/setupService";

/** What the server-side upload will accept. */
const ALLOWED_UPLOAD_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/x-icon",
  "image/vnd.microsoft.icon",
];

/**
 * One branding image: preview, file upload, and the URL it landed at.
 * The URL stays editable so images can also come from anywhere else.
 */
function ImageField({
  label,
  helperText,
  value,
  onChange,
  slug,
  kind,
  uploading,
  setUploading,
}) {
  const [error, setError] = useState("");

  const pickFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // allow re-picking the same file
    if (!file) return;

    setError("");

    if (file.size > 2 * 1024 * 1024) {
      setError("That image is bigger than 2MB");
      return;
    }

    if (!ALLOWED_UPLOAD_TYPES.includes(file.type)) {
      setError(`Unsupported file type ${file.type || "(unknown)"}`);
      return;
    }

    setUploading(kind);

    try {
      const dataUrl = await fileToDataUrl(file);
      const response = await uploadOrganisationAsset({
        file: dataUrl,
        slug: slug || "new",
        kind,
      });
      onChange(response.data.url);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Upload failed");
    } finally {
      setUploading(null);
    }
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center">
        <Box
          sx={{
            width: 72,
            height: 72,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "8px",
            backgroundColor: "background.paper",
            overflow: "hidden",
          }}
        >
          {value ? (
            <Box
              component="img"
              src={value}
              alt=""
              sx={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />
          ) : (
            <Typography variant="caption" color="text.secondary" align="center">
              {label}
            </Typography>
          )}
        </Box>

        <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
          <Button
            component="label"
            variant="outlined"
            size="small"
            disabled={Boolean(uploading)}
            sx={{ alignSelf: "flex-start" }}
          >
            {uploading === kind ? "Uploading..." : "Upload"}
            <input
              type="file"
              accept={ALLOWED_UPLOAD_TYPES.join(",")}
              hidden
              onChange={pickFile}
            />
          </Button>

          <TextField
            label={label}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            helperText={error || helperText}
            error={Boolean(error)}
            fullWidth
          />
        </Stack>
      </Stack>
    </Box>
  );
}

/**
 * Admin CRUD for the organisations that get their own public ranking page.
 *
 * Adding a district here plus a DNS CNAME is the whole onboarding process -
 * no deploy is involved. Uses local state rather than a Redux slice because
 * nothing else in the app reads this data.
 */

// Levels come from the database now (the level table managed in Ranking Setup).

const EMPTY = {
  slug: "",
  name: "",
  short_name: "",
  scope: "district",
  level_ids: [4],
  tier_ids: [],
  gender_tabs: ["Men", "Women", "All"],
  district_name: "",
  district_id: "",
  ranking_mode: "rolling",
  season_start_date: "",
  season_end_date: "",
  logo_url: "",
  favicon_url: "",
  og_image_url: "",
  primary_color: "#000000",
  secondary_color: "",
  accent_color: "",
  default_mode: "light",
  hero_title: "",
  hero_subtitle: "",
  custom_domain: "",
  is_active: true,
};

function toForm(org) {
  const form = { ...EMPTY };

  Object.keys(EMPTY).forEach((key) => {
    const value = org[key];
    if (value === null || value === undefined) return;
    form[key] = value;
  });

  form.level_ids = Array.isArray(org.level_ids) ? org.level_ids : [];
  form.tier_ids = Array.isArray(org.tier_ids) ? org.tier_ids : [];
  form.gender_tabs = Array.isArray(org.gender_tabs)
    ? org.gender_tabs
    : ["Men", "Women", "All"];
  return form;
}

/** Strip blank optional fields so they land as NULL rather than "". */
function toPayload(form) {
  const payload = { ...form };

  Object.entries(payload).forEach(([key, value]) => {
    if (typeof value === "string" && value.trim() === "") payload[key] = null;
  });

  payload.level_ids = form.level_ids;
  payload.tier_ids = form.tier_ids || [];
  payload.gender_tabs = form.gender_tabs || ["All"];
  payload.is_active = Boolean(form.is_active);

  if (payload.ranking_mode !== "season") {
    payload.season_start_date = null;
    payload.season_end_date = null;
  }

  return payload;
}

export default function Organisations() {
  const [organisations, setOrganisations] = useState([]);
  const [levels, setLevels] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [uploading, setUploading] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // org pending delete

  const load = useCallback(async () => {
    setIsLoading(true);
    setError("");

    try {
      const [response, lookupResponse] = await Promise.all([
        organisationService.listOrganisations(),
        getLookup(),
      ]);
      setOrganisations(response.data.organisations || []);
      setLevels(
        (lookupResponse.data?.levels || []).map((level) => ({
          id: level.id,
          label: level.name,
        })),
      );
      setTiers(lookupResponse.data?.tiers || []);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to load organisations",
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing("new");
    setForm(EMPTY);
    setFormError("");
  };

  const openEdit = (org) => {
    setEditing(org.id);
    setForm(toForm(org));
    setFormError("");
  };

  const closeDialog = () => {
    setEditing(null);
    setFormError("");
  };

  const setField = (key) => (event) => {
    const value =
      event.target.type === "checkbox"
        ? event.target.checked
        : event.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const save = async () => {
    setIsSaving(true);
    setFormError("");

    try {
      const payload = toPayload(form);

      if (editing === "new") {
        await organisationService.createOrganisation(payload);
        setNotice(`Created ${payload.name}`);
      } else {
        await organisationService.updateOrganisation(editing, payload);
        setNotice(`Updated ${payload.name}`);
      }

      closeDialog();
      await load();
    } catch (err) {
      setFormError(
        err.response?.data?.message || err.message || "Failed to save",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const remove = (org) => {
    setDeleteTarget(org);
  };

  const confirmRemove = async () => {
    setIsSaving(true);

    try {
      await organisationService.deleteOrganisation(deleteTarget.id);
      setNotice(`Deleted ${deleteTarget.name}`);
      setDeleteTarget(null);
      await load();
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed to delete",
      );
      setDeleteTarget(null);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          gap: 2,
        }}
      >
        <Typography variant="h4">Organisations</Typography>
        <Button variant="contained" onClick={openCreate}>
          Add organisation
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Each organisation gets a public ranking page. Point its domain here with
        a CNAME, add that domain in Coolify so a certificate is issued, then set
        it below.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}

      {organisations.length === 0 ? (
        <Typography variant="body1" color="text.secondary" sx={{ py: 4 }}>
          No organisations yet.
        </Typography>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Slug</TableCell>
                <TableCell>Counts</TableCell>
                <TableCell>Domain</TableCell>
                <TableCell>Window</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {organisations.map((org) => (
                <TableRow key={org.id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          width: 14,
                          height: 14,
                          borderRadius: "3px",
                          backgroundColor: org.primary_color,
                          border: "1px solid",
                          borderColor: "divider",
                          flexShrink: 0,
                        }}
                      />
                      <span>{org.name}</span>
                      {!org.is_active && <Chip size="small" label="inactive" />}
                    </Stack>
                  </TableCell>
                  <TableCell>{org.slug}</TableCell>
                  <TableCell>
                    {(org.level_ids || [])
                      .map(
                        (id) =>
                          levels.find((level) => level.id === id)?.label || id,
                      )
                      .join(", ")}
                    {org.district_name ? ` - ${org.district_name}` : ""}
                  </TableCell>
                  <TableCell>{org.custom_domain || "-"}</TableCell>
                  <TableCell>
                    {org.ranking_mode === "season"
                      ? `${org.season_start_date} to ${org.season_end_date}`
                      : "Rolling 3 years"}
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => openEdit(org)}>
                      Edit
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => remove(org)}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      <Dialog
        open={editing !== null}
        onClose={closeDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editing === "new" ? "Add organisation" : "Edit organisation"}
        </DialogTitle>

        <DialogContent dividers>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}

          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Name"
                value={form.name}
                onChange={setField("name")}
                fullWidth
                required
              />
              <TextField
                label="Short name"
                value={form.short_name}
                onChange={setField("short_name")}
                fullWidth
              />
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Slug"
                value={form.slug}
                onChange={setField("slug")}
                helperText="Used for the preview URL /rankings/<slug>"
                fullWidth
                required
              />
              <TextField
                label="Custom domain"
                value={form.custom_domain}
                onChange={setField("custom_domain")}
                helperText="e.g. rankings.wpbowls.co.za"
                fullWidth
              />
            </Stack>

            <Typography variant="h6" sx={{ pt: 1 }}>
              What counts
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel id="org-scope">Scope</InputLabel>
                <Select
                  labelId="org-scope"
                  label="Scope"
                  value={form.scope}
                  onChange={setField("scope")}
                >
                  <MenuItem value="district">District</MenuItem>
                  <MenuItem value="national">National</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth required>
                <InputLabel id="org-levels">Levels</InputLabel>
                <Select
                  labelId="org-levels"
                  multiple
                  value={form.level_ids}
                  onChange={setField("level_ids")}
                  input={<OutlinedInput label="Levels" />}
                  renderValue={(selected) =>
                    selected
                      .map(
                        (id) =>
                          levels.find((level) => level.id === id)?.label || id,
                      )
                      .join(", ")
                  }
                >
                  {levels.map((level) => (
                    <MenuItem key={level.id} value={level.id}>
                      <Checkbox checked={form.level_ids.includes(level.id)} />
                      <ListItemText primary={level.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel id="org-tiers">Additional tiers</InputLabel>
                <Select
                  labelId="org-tiers"
                  multiple
                  value={form.tier_ids || []}
                  onChange={setField("tier_ids")}
                  input={<OutlinedInput label="Additional tiers" />}
                  renderValue={(selected) =>
                    (selected || [])
                      .map(
                        (id) =>
                          tiers.find((tier) => tier.id === id)?.name || id,
                      )
                      .join(", ")
                  }
                >
                  {tiers.map((tier) => (
                    <MenuItem key={tier.id} value={tier.id}>
                      <Checkbox checked={(form.tier_ids || []).includes(tier.id)} />
                      <ListItemText
                        primary={tier.name}
                        secondary={tier.description}
                      />
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Entries at these tiers also count, from any district (e.g.
                  Classics). Leave empty for none.
                </Typography>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel id="org-gender-tabs">Ranking tabs</InputLabel>
                <Select
                  labelId="org-gender-tabs"
                  multiple
                  value={form.gender_tabs || []}
                  onChange={setField("gender_tabs")}
                  input={<OutlinedInput label="Ranking tabs" />}
                  renderValue={(selected) => (selected || []).join(", ")}
                >
                  {["Men", "Women", "All"].map((tab) => (
                    <MenuItem key={tab} value={tab}>
                      <Checkbox checked={(form.gender_tabs || []).includes(tab)} />
                      <ListItemText primary={tab} />
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Which gender tabs the public page shows (Men first, then
                  Women, then All). Pick only All while there is no gender data.
                </Typography>
              </FormControl>
            </Stack>

            {form.scope === "district" && (
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label="District name"
                  value={form.district_name}
                  onChange={setField("district_name")}
                  helperText="Must match the district on entries exactly"
                  fullWidth
                />
                <TextField
                  label="District ID"
                  value={form.district_id}
                  onChange={setField("district_id")}
                  helperText="Optional BSA district id"
                  fullWidth
                />
              </Stack>
            )}

            <Typography variant="h6" sx={{ pt: 1 }}>
              Ranking window
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel id="org-window">Window</InputLabel>
                <Select
                  labelId="org-window"
                  label="Window"
                  value={form.ranking_mode}
                  onChange={setField("ranking_mode")}
                >
                  <MenuItem value="rolling">
                    Rolling 3 years (weighted)
                  </MenuItem>
                  <MenuItem value="season">Season dates (unweighted)</MenuItem>
                </Select>
              </FormControl>

              {form.ranking_mode === "season" && (
                <>
                  <TextField
                    label="Season start"
                    type="date"
                    value={form.season_start_date || ""}
                    onChange={setField("season_start_date")}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <TextField
                    label="Season end"
                    type="date"
                    value={form.season_end_date || ""}
                    onChange={setField("season_end_date")}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </>
              )}
            </Stack>

            <Typography variant="h6" sx={{ pt: 1 }}>
              Branding
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Primary colour"
                value={form.primary_color}
                onChange={setField("primary_color")}
                helperText="Hex, e.g. #0B3D91"
                fullWidth
              />
              <TextField
                label="Secondary colour"
                value={form.secondary_color}
                onChange={setField("secondary_color")}
                fullWidth
              />
              <TextField
                label="Accent colour"
                value={form.accent_color}
                onChange={setField("accent_color")}
                fullWidth
              />
            </Stack>

            <ImageField
              label="Logo"
              helperText="Square or horizontal logo, shown in the header"
              value={form.logo_url}
              onChange={(url) => setForm((prev) => ({ ...prev, logo_url: url }))}
              slug={form.slug}
              kind="logo"
              uploading={uploading}
              setUploading={setUploading}
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <ImageField
                label="Favicon"
                value={form.favicon_url}
                onChange={(url) =>
                  setForm((prev) => ({ ...prev, favicon_url: url }))
                }
                slug={form.slug}
                kind="favicon"
                uploading={uploading}
                setUploading={setUploading}
              />
              <ImageField
                label="Share image"
                helperText="Shown when the page is shared on WhatsApp etc."
                value={form.og_image_url}
                onChange={(url) =>
                  setForm((prev) => ({ ...prev, og_image_url: url }))
                }
                slug={form.slug}
                kind="share"
                uploading={uploading}
                setUploading={setUploading}
              />
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Heading"
                value={form.hero_title}
                onChange={setField("hero_title")}
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel id="org-mode">Default appearance</InputLabel>
                <Select
                  labelId="org-mode"
                  label="Default appearance"
                  value={form.default_mode}
                  onChange={setField("default_mode")}
                >
                  <MenuItem value="light">Light</MenuItem>
                  <MenuItem value="dark">Dark</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            <TextField
              label="Intro text"
              value={form.hero_subtitle}
              onChange={setField("hero_subtitle")}
              multiline
              minRows={2}
              fullWidth
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(form.is_active)}
                  onChange={setField("is_active")}
                />
              }
              label="Active (the public page is live)"
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeDialog} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={save} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation - replaces the browser confirm */}
      <Dialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete organisation</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Delete {deleteTarget?.name}? Its public page stops working. This
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmRemove}
            disabled={isSaving}
          >
            {isSaving ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
